import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { invoiceAPI, paymentAPI } from "../lib/api";
import { formatCurrency, formatDate, getStatusColor, getStatusLabel, numberToWords } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Download,
  Printer,
  Edit,
  CreditCard,
  Loader2,
  Check,
  X,
} from "lucide-react";
import { toast } from "sonner";

export default function InvoiceViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useAuth();
  const printRef = useRef(null);

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_mode: "bank_transfer",
    reference_number: "",
    notes: "",
  });
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await invoiceAPI.get(id);
      setInvoice(response.data);
      setPaymentData((prev) => ({
        ...prev,
        amount: response.data.balance_due || 0,
      }));
    } catch (error) {
      toast.error("Failed to load invoice");
      navigate("/invoices");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await invoiceAPI.getPDF(id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.invoice_number.replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    
    if (paymentData.amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSavingPayment(true);
    try {
      await paymentAPI.create({
        invoice_id: invoice.id,
        client_id: invoice.client_id,
        amount: parseFloat(paymentData.amount),
        payment_mode: paymentData.payment_mode,
        reference_number: paymentData.reference_number,
        notes: paymentData.notes,
      });
      toast.success("Payment recorded successfully");
      setPaymentDialogOpen(false);
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSavingPayment(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await invoiceAPI.update(id, { status: newStatus });
      toast.success("Status updated");
      fetchInvoice();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDateClean = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Format company name to title case with Pvt Ltd
  const formatCompanyName = (name) => {
    if (!name) return "";
    return name
      .replace('PRIVATE LIMITED', 'Pvt Ltd')
      .replace('Private Limited', 'Pvt Ltd')
      .split(' ')
      .map(word => {
        if (word.toUpperCase() === 'PVT') return 'Pvt';
        if (word.toUpperCase() === 'LTD') return 'Ltd';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!invoice) return null;

  const company = invoice.company || {};
  const client = invoice.client || {};
  const isProforma = invoice.is_proforma || invoice.invoice_type === "proforma_invoice";
  const companyNameFormatted = formatCompanyName(company.company_name || "Black Coffee Communication Pvt Ltd");

  return (
    <div className="space-y-6" data-testid="invoice-view-page">
      {/* Header Actions - No Print */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/invoices")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 font-mono">{invoice.invoice_number}</h1>
            <Badge className={getStatusColor(invoice.status)}>
              {getStatusLabel(invoice.status)}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit() && invoice.status !== "paid" && (
            <>
              <Button variant="outline" onClick={() => navigate(`/invoices/${id}/edit`)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline" onClick={() => setPaymentDialogOpen(true)}>
                <CreditCard className="w-4 h-4 mr-2" />
                Record Payment
              </Button>
            </>
          )}
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button onClick={handleDownloadPDF} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Status Actions - No Print */}
      {canEdit() && invoice.status !== "paid" && invoice.status !== "cancelled" && (
        <Card className="no-print">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-slate-500">Quick Actions:</span>
              {invoice.status === "draft" && (
                <Button size="sm" variant="outline" onClick={() => handleStatusChange("sent")}>
                  <Check className="w-4 h-4 mr-1" />
                  Mark as Sent
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => handleStatusChange("cancelled")}
              >
                <X className="w-4 h-4 mr-1" />
                Cancel Invoice
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ========== UPDATED INVOICE PREVIEW TO MATCH PDF ========== */}
      <div id="invoice-preview" ref={printRef} className="inv-paper">
        
        {/* ===== TOP HEADER BAR ===== */}
        <div className="inv-top-bar"></div>
        
        {/* ===== MAIN HEADER SECTION - Logo (150% larger) ===== */}
        <div className="inv-header-main">
          {/* Left: Logo only */}
          <div className="inv-logo-section">
            <img 
              src="https://customer-assets.emergentagent.com/job_69a7b500-1029-49a2-8f5d-3a190c16d5d0/artifacts/7e0x0p4v_logo%20white%20bg.png" 
              alt="Company Logo" 
              className="inv-logo"
              style={{ width: '220px', height: 'auto' }}
            />
          </div>

          {/* Right: Invoice Title & Details + PO Number */}
          <div className="inv-title-section">
            <div className="inv-type-title">
              {isProforma ? "PROFORMA INVOICE" : "TAX INVOICE"}
            </div>
            <div className="text-xs text-slate-500 mb-2">Original Copy</div>
            <div className="inv-details-grid">
              <div className="inv-detail-item">
                <span className="inv-detail-label">Invoice No.</span>
                <span className="inv-detail-value">{invoice.invoice_number}</span>
              </div>
              <div className="inv-detail-item">
                <span className="inv-detail-label">Date</span>
                <span className="inv-detail-value">{formatDateClean(invoice.invoice_date)}</span>
              </div>
              {invoice.po_number && (
                <div className="inv-detail-item">
                  <span className="inv-detail-label">P.O. Number</span>
                  <span className="inv-detail-value">{invoice.po_number}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* GSTIN moved closer to Bill To box (no grey line) */}
        <div className="mt-2 mb-3 text-sm font-medium text-slate-700">
          <span className="font-bold">GSTIN:</span> {company.gstin || "07AANCB3150C1Z1"}
        </div>

        {/* ===== BILL TO & SUPPLY DETAILS ===== */}
        <div className="inv-parties">
          <div className="inv-party-card">
            <div className="inv-party-header">Bill To</div>
            <div className="inv-party-body">
              <div className="inv-party-name">M/S {client.name}</div>
              {client.address_line1 && <div>{client.address_line1}</div>}
              {client.address_line2 && <div>{client.address_line2}</div>}
              <div>{[client.city, client.state, client.pincode].filter(Boolean).join(", ")}</div>
              {client.gstin && <div className="inv-party-gstin"><b>GSTIN:</b> {client.gstin}</div>}
              {client.phone && <div><b>Phone:</b> {client.phone}</div>}
            </div>
          </div>
          <div className="inv-party-card">
            <div className="inv-party-header">Supply & Transport Details</div>
            <div className="inv-party-body">
              <div className="inv-supply-grid">
                <div className="inv-supply-item">
                  <span>Place of Supply</span>
                  <span>{invoice.place_of_supply || "-"}</span>
                </div>
                <div className="inv-supply-item">
                  <span>Reverse Charge</span>
                  <span>{invoice.reverse_charge ? "Yes" : "No"}</span>
                </div>
                <div className="inv-supply-item">
                  <span>Transport</span>
                  <span>{invoice.transport || "-"}</span>
                </div>
                <div className="inv-supply-item">
                  <span>Vehicle No.</span>
                  <span>{invoice.vehicle_no || "-"}</span>
                </div>
                <div className="inv-supply-item">
                  <span>GR/RR No.</span>
                  <span>{invoice.gr_rr_no || "-"}</span>
                </div>
                <div className="inv-supply-item">
                  <span>E-Way Bill</span>
                  <span>{invoice.eway_bill || "-"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== ITEMS TABLE with Product Name (bold) + Description (grey, smaller) ===== */}
        <div className="inv-items-section">
          <table className="inv-items-table">
            <thead>
              <tr>
                <th className="inv-col-sno">#</th>
                <th className="inv-col-desc">Description</th>
                <th className="inv-col-hsn">HSN/SAC</th>
                <th className="inv-col-qty">Qty</th>
                <th className="inv-col-rate">Rate</th>
                <th className="inv-col-taxable">Taxable</th>
                <th className="inv-col-gst">GST</th>
                <th className="inv-col-amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items?.map((item, index) => (
                <tr key={index}>
                  <td className="inv-td-sno">{index + 1}</td>
                  <td className="inv-td-desc">
                    {/* Product Name (bold) */}
                    <div className="font-bold text-slate-900">
                      {item.product_name || item.description}
                    </div>
                    {/* Optional Description (smaller, grey) - only show if different from product_name */}
                    {item.description && item.product_name && item.description !== item.product_name && (
                      <div className="text-xs text-slate-500 mt-1" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                        {item.description}
                      </div>
                    )}
                  </td>
                  <td className="inv-td-hsn">{item.hsn_sac_code}</td>
                  <td className="inv-td-qty">{item.quantity} {item.unit}</td>
                  <td className="inv-td-rate">₹{formatAmount(item.unit_price)}</td>
                  <td className="inv-td-taxable">₹{formatAmount(item.taxable_amount)}</td>
                  <td className="inv-td-gst">{item.tax_rate || 18}%</td>
                  <td className="inv-td-amount">₹{formatAmount(item.total_amount || (item.taxable_amount + item.tax_amount))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ===== BOTTOM SECTION: Amount in Words (grey box) + Totals ===== */}
        <div className="inv-totals-section">
          {/* Left: Amount in Words (Grey Box) + Payment Terms + Bank Details */}
          <div className="flex-1 space-y-4">
            {/* Amount in Words - GREY BOX */}
            <div className="bg-slate-100 border border-slate-300 p-3 rounded">
              <div className="text-xs font-bold text-slate-700 uppercase mb-1">Amount in Words</div>
              <div className="text-sm font-medium text-slate-900">{numberToWords(invoice.grand_total)}</div>
            </div>
            
            {/* Payment Terms (editable in invoice) */}
            <div>
              <div className="text-xs font-bold text-slate-700 mb-1">Payment Terms</div>
              <div className="text-sm text-slate-700">{invoice.terms || "100% advance payment to execute order"}</div>
            </div>
            
            {/* Bank Details - Company name in title case with Pvt Ltd */}
            <div>
              <div className="text-xs font-bold text-slate-700 mb-1">Bank Details</div>
              <div className="text-sm text-slate-700 space-y-0.5">
                <div className="font-bold">{companyNameFormatted}</div>
                <div>Bank: {company.bank_name || "ICICI Bank"}</div>
                <div>A/C No: {company.bank_account || "589705500029"}</div>
                <div>IFSC: {company.bank_ifsc || "ICIC0005897"}</div>
                {company.bank_upi && <div>UPI: {company.bank_upi}</div>}
              </div>
            </div>
          </div>
          
          {/* Right: Totals + Signature (right-aligned, signature bottom-aligned with UPI) */}
          <div className="flex flex-col items-end justify-between">
            {/* Totals Box */}
            <div className="inv-totals-box">
              <div className="inv-total-row">
                <span>Subtotal</span>
                <span>₹{formatAmount(invoice.taxable_amount)}</span>
              </div>
              {invoice.tax_type === "igst" ? (
                <div className="inv-total-row">
                  <span>IGST (18%)</span>
                  <span>₹{formatAmount(invoice.igst_amount)}</span>
                </div>
              ) : invoice.tax_type === "cgst_sgst" ? (
                <>
                  <div className="inv-total-row">
                    <span>CGST (9%)</span>
                    <span>₹{formatAmount(invoice.cgst_amount)}</span>
                  </div>
                  <div className="inv-total-row">
                    <span>SGST (9%)</span>
                    <span>₹{formatAmount(invoice.sgst_amount)}</span>
                  </div>
                </>
              ) : null}
              {invoice.round_off !== 0 && invoice.round_off !== undefined && (
                <div className="inv-total-row inv-roundoff">
                  <span>Round Off</span>
                  <span>₹{formatAmount(invoice.round_off)}</span>
                </div>
              )}
              <div className="inv-grand-total-row">
                <span>GRAND TOTAL</span>
                <span>₹{formatAmount(invoice.grand_total)}</span>
              </div>
            </div>
            
            {/* Signature - Right Aligned, Bold Company Name, Bottom aligned with UPI */}
            <div className="text-right mt-auto">
              <div className="text-sm font-bold text-slate-900">For {companyNameFormatted}</div>
              <div className="h-12 mt-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_gst-invoice-5/artifacts/crvjslso_signature.jpg" 
                  alt="Signature" 
                  className="h-10 ml-auto"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
              <div className="text-xs text-slate-500 mt-1">Authorised Signatory</div>
            </div>
          </div>
        </div>

        {/* ===== FOOTER - Centered ===== */}
        <div className="mt-6 pt-4 border-t border-slate-300 text-center text-xs text-slate-500">
          Registered Office: N-161, Saira Tower G.F, Gautam Nagar, Near Green Park Metro Station, New Delhi 110049 &nbsp;|&nbsp; m: +91 9911 30 2025 &nbsp;|&nbsp; e: info@bc2.in
        </div>

        {/* ===== BOTTOM BAR ===== */}
        <div className="inv-bottom-bar"></div>
      </div>

      {/* Payment History - No Print */}
      {invoice.payments?.length > 0 && (
        <Card className="no-print">
          <CardHeader>
            <CardTitle>Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invoice.payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-mono text-sm">{payment.receipt_number}</p>
                    <p className="text-sm text-slate-500">
                      {formatDate(payment.payment_date)} • {payment.payment_mode.replace("_", " ")}
                    </p>
                    {payment.reference_number && (
                      <p className="text-xs text-slate-400">Ref: {payment.reference_number}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold text-emerald-600">{formatCurrency(payment.amount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Invoice Total:</span>
                <span className="font-mono">{formatCurrency(invoice.grand_total)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Amount Paid:</span>
                <span className="font-mono text-emerald-600">{formatCurrency(invoice.amount_paid)}</span>
              </div>
              <div className="flex justify-between font-medium pt-2 border-t mt-2">
                <span>Balance Due:</span>
                <span className="font-mono text-amber-600">{formatCurrency(invoice.balance_due)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentData.amount}
                onChange={(e) => setPaymentData((prev) => ({ ...prev, amount: e.target.value }))}
                className="font-mono"
                data-testid="payment-amount"
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <Select
                value={paymentData.payment_mode}
                onValueChange={(value) => setPaymentData((prev) => ({ ...prev, payment_mode: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Reference Number</Label>
              <Input
                value={paymentData.reference_number}
                onChange={(e) => setPaymentData((prev) => ({ ...prev, reference_number: e.target.value }))}
                placeholder="Transaction ID, Cheque No., etc."
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={paymentData.notes}
                onChange={(e) => setPaymentData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingPayment} data-testid="save-payment-btn">
                {savingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Record Payment"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
